import React, { createContext, useContext, useState, ReactNode } from 'react';
import { AccessibilityContextProps } from '../../interfaces/props/AccessibilityContextProps';
import { calculateFont, calculateFontWithMargin } from '../../utils/CalculateFont';
import { SxProps } from '@mui/material';

const AccessibilityContext = createContext<AccessibilityContextProps | undefined>(undefined);

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
};

interface AccessibilityProviderProps {
  children: ReactNode;
}

export const AccessibilityProvider: React.FC<AccessibilityProviderProps> = ({ children }) => {
  const [contrastMode, setContrastMode] = useState(false);
  const [fontSize, setFontSize] = useState(16);
  const [fontSizeTitle, setFontSizeTitle] = useState(24);
  const [alterFontSizeMode, setAlterFontSizeMode] = useState(false);

  const toggleContrastMode = () => {
    const nextMode = !contrastMode;
    setContrastMode(nextMode);
    document.documentElement.style.setProperty(
      '--focus-color', 
      nextMode ? 'white' : 'black'
    );

    document.documentElement.style.setProperty(
      '--button-disabled-background-color', 
      nextMode ? 'rgba(255, 255, 0, 0.4)' : 'rgba(0, 0, 0, 0.12)'
    );

  };

  const increaseFontSize = () => {
    setFontSize((prevSize) => Math.min(prevSize + 1, 30));
    setFontSizeTitle(((prevSize) => Math.min(prevSize + 1, 30)));
    setAlterFontSizeMode(true)
  };

  const decreaseFontSize = () => {
    setFontSize((prevSize) => Math.max(prevSize - 1, 10));
    setFontSizeTitle(((prevSize) => Math.max(prevSize - 1, 10)));
    setAlterFontSizeMode(true)
  };

  const resetFontSize = () => {
    setAlterFontSizeMode(false)
    setFontSize(16)
    setFontSizeTitle(24);
  }

  const contrastStyle: React.CSSProperties = {
    backgroundColor: contrastMode ? 'black' : '',
    background: contrastMode ? 'black' : '',
    color: contrastMode ? 'white' : '',
  };

  const calculateTextStyle = (defaultFontSize: number) : React.CSSProperties => ({
    ...contrastStyle,
    ...calculateFont(defaultFontSize + fontSize - 16),
  });

  const calculateTextStyleWithMargin = (defaultFontSize: number) : React.CSSProperties => ({
    ...contrastStyle,
    ...calculateFontWithMargin(defaultFontSize + fontSize - 16),
  });

  const calculateFontStyle = (defaultFontSize: number) : React.CSSProperties => calculateFont(defaultFontSize + fontSize - 16);

  const calculateFontStyleWithMargin = (defaultFontSize: number) : React.CSSProperties => calculateFontWithMargin(defaultFontSize + fontSize - 16);

  const textStyleAccessibility: React.CSSProperties = {
    ...contrastStyle,
    ...calculateFont(fontSize),
    fontSize: alterFontSizeMode ? `${fontSize}px` : '',
  };

  const checkboxStyleAccessibility: React.CSSProperties = {
    ...contrastStyle,
    ...calculateFont(fontSize),
    fontSize: alterFontSizeMode ? `${fontSize}px` : '',
    background: 'none'
  };


  const textStyleAccessibilityWithMargin: React.CSSProperties = {
    ...contrastStyle,
    ...calculateFontWithMargin(fontSize),
    fontSize: alterFontSizeMode ? `${fontSize}px` : '',
  };

  const textTitleStyleAccessibility: React.CSSProperties = {
    ...contrastStyle,
    ...calculateFont(fontSizeTitle),
    fontSize: alterFontSizeMode ? `${fontSizeTitle}px` : ''
  }

  const fontSizeStyleAccessibility: React.CSSProperties = {
    ...calculateFont(fontSize),
    fontSize: alterFontSizeMode ? `${fontSize}px` : ''
  };

  const fontSizeTitleStyleAccessibility: React.CSSProperties = {
    ...calculateFont(fontSizeTitle),
    fontSize: alterFontSizeMode ? `${fontSizeTitle}px` : ''
  };

  const contrastColor = '#FFFF00';

  const buttonStyleAccessibility: React.CSSProperties = {
    backgroundColor: contrastMode ? contrastColor : '',
  };

  const buttonTextStyleAccessibility: React.CSSProperties = {
    fontFamily: 'UnimedSans-Bold',
    color: contrastMode ? 'black' : '',
    fontWeight: contrastMode  ? '700' : '' ,
    ...calculateFont(fontSize),
    fontSize: alterFontSizeMode ? `${fontSize}px` : '',
  };

  const contrastBorderAccessibility: React.CSSProperties = {
    border: contrastMode ? '1px solid white' : ''
  }

  const contrastColorAccessibility: React.CSSProperties = {
    color: contrastMode ? contrastColor : '',
  }


  const values: AccessibilityContextProps = {
    alterFontSizeMode,
    contrastMode,
    fontSize,
    fontSizeStyleAccessibility,
    fontSizeTitleStyleAccessibility,
    toggleContrastMode,
    increaseFontSize,
    decreaseFontSize,
    resetFontSize,
    calculateTextStyle,
    calculateTextStyleWithMargin,
    calculateFontStyle,
    calculateFontStyleWithMargin,
    contrastStyle,
    contrastColor,
    contrastBorderAccessibility,
    contrastColorAccessibility,
    textStyleAccessibility,
    textStyleAccessibilityWithMargin,
    textTitleStyleAccessibility,
    buttonStyleAccessibility,
    buttonTextStyleAccessibility,
    checkboxStyleAccessibility
  };

  return (
    <AccessibilityContext.Provider value={values}>
      {children}
    </AccessibilityContext.Provider>
  );
};
